// Keep this browser contract in parity with functions/billingAccess.js; subscriptionAccess.test.js checks both.
const DAY_MS = 24 * 60 * 60 * 1000;

const toTimestamp = (value) => {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    if (typeof value === 'string' && value.trim()) {
        const numeric = Number(value);
        if (Number.isFinite(numeric) && numeric > 0) return numeric;
        const parsed = Date.parse(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }
    if (value && typeof value === 'object') {
        const seconds = Number(value.seconds ?? value._seconds);
        if (Number.isFinite(seconds) && seconds > 0) return seconds * 1000;
    }
    return null;
};

const BILLABLE_ACCOUNT_TYPES = new Set(['printShop', 'specialist', 'supplier']);
const isBillableAccount = (user) => Boolean(user && BILLABLE_ACCOUNT_TYPES.has(user.type) && user.status === 'approved');

const getBillingAccess = (user, now = Date.now()) => {
    const plan = user?.plan || {};
    const paidThroughAt = toTimestamp(plan.paidThroughAt);
    const graceUntilAt = toTimestamp(plan.graceUntilAt);
    const trialEndsAt = toTimestamp(plan.trialEndsAt);
    const approved = isBillableAccount(user);
    const pendingStatus = user?.status === 'payment_pending' || ['awaiting_payment', 'awaiting_approval'].includes(plan.subscriptionStatus);

    let state;
    let accessUntilAt = null;
    if (!approved) {
        state = pendingStatus ? 'awaiting_payment' : 'awaiting_approval';
    } else if (!plan.cancelAtPeriodEnd && (plan.billingState === 'grace' || plan.subscriptionStatus === 'past_due') && graceUntilAt && now < graceUntilAt) {
        state = 'grace';
        accessUntilAt = graceUntilAt;
    } else if (paidThroughAt && now < paidThroughAt) {
        state = !plan.cancelAtPeriodEnd && plan.billingState === 'grace' && graceUntilAt && now < graceUntilAt ? 'grace' : 'active';
        accessUntilAt = state === 'grace' ? graceUntilAt : paidThroughAt;
    } else if (trialEndsAt && now < trialEndsAt && !paidThroughAt) {
        state = 'trial';
        accessUntilAt = trialEndsAt;
    } else if (plan.billingReconciliationRequired === true
        || (['active', 'non-renewing', 'attention'].includes(plan.subscriptionStatus) && !paidThroughAt && !trialEndsAt)) {
        state = 'reconciliation_required';
    } else if (pendingStatus) {
        state = plan.subscriptionStatus === 'awaiting_payment' ? 'awaiting_payment' : 'awaiting_approval';
    } else {
        state = 'expired';
    }

    const hasService = approved && ['active', 'trial', 'grace'].includes(state);
    const daysRemaining = accessUntilAt && accessUntilAt > now
        ? Math.max(1, Math.ceil((accessUntilAt - now) / DAY_MS))
        : 0;

    return {
        state,
        hasAccountAccess: approved || user?.type === 'admin',
        canPublishListing: hasService,
        canCreateJob: hasService,
        canSendEnquiry: hasService,
        paidThroughAt,
        graceUntilAt,
        daysRemaining,
        requiresPayment: approved && ['expired', 'reconciliation_required'].includes(state),
    };
};

const hasFullServiceAccess = (user, now = Date.now()) => {
    const access = getBillingAccess(user, now);
    return access.canPublishListing && access.canCreateJob && access.canSendEnquiry;
};

export { DAY_MS, getBillingAccess, hasFullServiceAccess, isBillableAccount, toTimestamp };
